import { Component, inject, input, signal, viewChild } from "@angular/core";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faBell, faQuestion, faSearch } from "@fortawesome/free-solid-svg-icons";
import { NgbDropdownModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../../../core/utils";
import { Language } from "../../../../../constants/enums";
import { SmartToolTipsService } from "../../../../../directives";
import { ContextualHelpContent } from "../../../../../models/online-help-model";
import { OnlineHelpService } from "../../../../../services/online-help-service";
import { ContextualHelpModalComponent } from "../../../../modals/contextual-help-modal/contextual-help-modal";

@Component({
  selector: "adaa-activity-bar",
  styleUrls: ["./styles.scss"],
  imports: [
    FontAwesomeModule,
    TranslatePipe,
    RouterLink,
    RouterLinkActive,
    NgbDropdownModule,
    ContextualHelpModalComponent,
  ],
  template: `
    <div class="topbar--helpful-actions" [class]="type()" [dir]="direction()">
      <button
        role="link"
        id="appSearch"
        class="btn btn-sm bg-card text-primary rounded-circle shadow-sm mx-3 d-inline-block hide-search-btn activity-bar-opts"
        [dir]="direction()"
        [routerLink]="['/', 'console', 'search']"
        routerLinkActive="active"
      >
        <fa-icon [icon]="icon.search" [title]="'breadcrumbs.search' | translate"></fa-icon>
      </button>

      <button
        role="link"
        id="notificationCenter"
        class="btn btn-sm bg-card text-primary rounded-circle shadow-sm position-relative activity-bar-opts"
        [class.text-primary]="!hasNotifications()"
        [class.text-info]="hasNotifications()"
        [routerLink]="['communications']"
      >
        <fa-icon [icon]="icon.notification" [title]="'tooltips.notification_center' | translate"></fa-icon>

        <!-- badge -->
        @if (hasNotifications()) {
          <span
            data-sms-count
            class="position-absolute translate-middle start-0 px-1 text-white bg-info border border-light rounded-pill"
          >
            {{ !count() ? "" : count() }}
            <span class="visually-hidden">
              {{ "communication.title" | translate }}
            </span>
          </span>
        }
      </button>

      <!-- TODO: Add dropdown items for helpdesk -->

      <div class="col">
        <div ngbDropdown class="d-inline-block">
          <button
            role="link"
            id="helpCenter"
            class="btn btn-sm bg-card text-primary rounded-circle shadow-sm mx-3 activity-bar-opts"
            [dir]="direction()"
            ngbDropdownToggle
          >
            <fa-icon [icon]="icon.helpdesk" [title]="'tooltips.help_center' | translate"></fa-icon>
          </button>
          <div ngbDropdownMenu aria-labelledby="helpCenter">
            @if (smartToolTipsService.hasToolTips()) {
              <button
                ngbDropdownItem
                [class.text-end]="AdaaHelper.getCurrentLang() === Language.Arabic"
                [class.tooltips-enabled]="smartToolTipsService.isSmartToolTipsEnabled()"
                (click)="smartToolTipsService.toggleToolTips()"
              >
                {{ "help.smart-tool-tips" | translate }}
              </button>
            }
            <button
              ngbDropdownItem
              [class.text-end]="AdaaHelper.getCurrentLang() === Language.Arabic"
              (click)="getContentByPath()"
            >
              {{ "help.contextual_help" | translate }}
            </button>
            <button
              ngbDropdownItem
              [class.text-end]="AdaaHelper.getCurrentLang() === Language.Arabic"
              [routerLink]="['/', 'console', 'help', 'faqs-search']"
            >
              {{ "topbar.help.faq" | translate }}
            </button>
            <button
              ngbDropdownItem
              [class.text-end]="AdaaHelper.getCurrentLang() === Language.Arabic"
              [routerLink]="['/', 'pmo-manual-web']"
            >
              {{ "topbar.help.entitymanual" | translate }}
            </button>
            <button
              ngbDropdownItem
              [class.text-end]="AdaaHelper.getCurrentLang() === Language.Arabic"
              [routerLink]="['/', 'pmo-manual-pdf']"
            >
              {{ "topbar.help.usermanual" | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <adaa-contextual-help-modal #help [contents]="this.contents()"></adaa-contextual-help-modal>
  `,
})
export class ActivityBarComponent {
  private _onlineHelpService = inject(OnlineHelpService);
  private _router = inject(Router);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);
  smartToolTipsService = inject(SmartToolTipsService);

  untilDestroy = AdaaHelper.untilDestroyed();
  count = input.required<string>();
  hasNotifications = input.required<boolean>();
  direction = input.required<"ltr" | "rtl">();
  type = input.required<"full" | "mini">();
  contents = signal<ContextualHelpContent[]>([]);
  contextualHelp = viewChild.required<ContextualHelpModalComponent>("help");
  AdaaHelper = AdaaHelper;
  Language = Language;

  public get icon() {
    return {
      search: faSearch,
      helpdesk: faQuestion,
      notification: faBell,
    };
  }

  public getContentByPath() {
    const path = this._router.url;
    this._onlineHelpService.geContentByPath(path).subscribe({
      next: (res) => {
        if (res.inError || !res.responseData.contents.length) {
          this.contents.set([]);
          this._toastrService.warning(this._translateService.instant("notification.no_contextual_help"));
        } else {
          this.contents.set(res.responseData.contents);
          this.contextualHelp().open();
        }
      },
    });
  }
}
